import React from 'react';
import { CheckCircle2, CircleAlert, Loader2, PauseCircle, PlayCircle } from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import type { AsyncJobMetadata } from '../../utils/asyncJobs';
import { cn } from '../../utils';
import AgentAvatar from '../Common/AgentAvatar';

interface AsyncJobCardProps {
  asyncJob: AsyncJobMetadata;
  agentLabel: string;
  agentAvatar?: string;
  timeReference: number;
  className?: string;
}

function formatElapsedTime(elapsedMs: number) {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function getStatusCopy(asyncJob: AsyncJobMetadata) {
  switch (asyncJob.status) {
    case 'completed':
      return {
        label: 'Response ready',
        tone: 'success' as const,
        icon: CheckCircle2,
        detail: asyncJob.progressMessage || 'Your answer is ready.',
        announcement: 'Response ready.',
        helperText: 'Ally finished this background job and attached the answer in the chat.',
      };
    case 'failed':
      return {
        label: 'Something went wrong',
        tone: 'error' as const,
        icon: CircleAlert,
        detail: asyncJob.progressMessage || 'Ally could not finish this job.',
        announcement: asyncJob.progressMessage || 'Something went wrong.',
        helperText: 'This job stopped before Ally could deliver a final answer.',
      };
    case 'cancelled':
      return {
        label: 'Updates paused',
        tone: 'muted' as const,
        icon: PauseCircle,
        detail: asyncJob.progressMessage || 'Stopped checking this job on this device.',
        announcement: asyncJob.progressMessage || 'Updates paused.',
        helperText: 'This only pauses local polling. The agent may still finish the underlying job in the background.',
      };
    default:
      return {
        label: 'Working',
        tone: 'processing' as const,
        icon: Loader2,
        detail: asyncJob.progressMessage || 'Thinking...',
        announcement: asyncJob.progressMessage || 'Still working.',
        helperText: 'You can switch chats or refresh. Ally will reconnect and keep checking from this browser.',
      };
  }
}

export function AsyncJobCard({ asyncJob, agentLabel, agentAvatar, timeReference, className }: AsyncJobCardProps) {
  const statusCopy = getStatusCopy(asyncJob);
  const Icon = statusCopy.icon;
  const cancelPendingJob = useChatStore((state) => state.cancelPendingJob);
  const resumePendingJob = useChatStore((state) => state.resumePendingJob);
  const startedAtMs = asyncJob.startedAt ? new Date(asyncJob.startedAt).getTime() : Number.NaN;
  const elapsedMs = Number.isFinite(startedAtMs)
    ? Math.max(asyncJob.elapsedMs, timeReference - startedAtMs)
    : asyncJob.elapsedMs;

  return (
    <div className={cn('flex justify-start', className)}>
      <div
        className={cn(
          'w-full max-w-xl rounded-[1.5rem] border px-4 py-3 shadow-sm',
          statusCopy.tone === 'processing' && 'border-blue-500/25 bg-blue-500/10 shadow-blue-950/10',
          statusCopy.tone === 'success' && 'border-emerald-500/25 bg-emerald-500/10 shadow-emerald-950/10',
          statusCopy.tone === 'error' && 'border-red-500/25 bg-red-500/10 shadow-red-950/10',
          statusCopy.tone === 'muted' && 'border-slate-500/25 bg-slate-500/10 shadow-slate-950/10',
        )}
      >
        <div className="sr-only" role={statusCopy.tone === 'error' ? 'alert' : 'status'} aria-live={statusCopy.tone === 'error' ? 'assertive' : 'polite'} aria-atomic="true">
          {agentLabel}. {statusCopy.label}. {statusCopy.announcement}
        </div>

        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <AgentAvatar agent={{ name: agentLabel, avatar: agentAvatar }} size="md" />
            <span
              aria-hidden="true"
              className={cn(
                'absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-card',
                statusCopy.tone === 'processing' && 'bg-blue-500',
                statusCopy.tone === 'success' && 'bg-emerald-500',
                statusCopy.tone === 'error' && 'bg-red-500',
                statusCopy.tone === 'muted' && 'bg-slate-500',
              )}
            >
              <Icon
                className={cn(
                  'h-2.5 w-2.5 text-white',
                  statusCopy.tone === 'processing' && 'ally-async-job-spinner',
                )}
              />
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold text-foreground-bright">{agentLabel}</div>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                  statusCopy.tone === 'processing' && 'border-blue-300/25 bg-blue-950/40 text-blue-100',
                  statusCopy.tone === 'success' && 'border-emerald-300/25 bg-emerald-950/40 text-emerald-100',
                  statusCopy.tone === 'error' && 'border-red-300/25 bg-red-950/40 text-red-100',
                  statusCopy.tone === 'muted' && 'border-slate-300/25 bg-slate-900/60 text-slate-200',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    statusCopy.tone === 'processing' && 'bg-blue-200 ally-async-job-dot',
                    statusCopy.tone === 'success' && 'bg-emerald-200',
                    statusCopy.tone === 'error' && 'bg-red-200',
                    statusCopy.tone === 'muted' && 'bg-slate-200',
                  )}
                />
                {statusCopy.label}
              </span>
            </div>

            <div className="mt-1 text-sm text-foreground">{statusCopy.detail}</div>
            <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{statusCopy.helperText}</div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span aria-hidden="true">Elapsed {formatElapsedTime(elapsedMs)}</span>
              <span>{asyncJob.startedAt ? `Started ${new Date(asyncJob.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Started just now'}</span>
            </div>

            {statusCopy.tone === 'processing' ? (
              <div aria-hidden="true" className="mt-3 h-1.5 overflow-hidden rounded-full bg-blue-950/45">
                <div className="ally-async-job-bar h-full w-1/2 rounded-full bg-gradient-to-r from-blue-400 via-sky-300 to-blue-500" />
              </div>
            ) : null}

            {asyncJob.status === 'processing' ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => cancelPendingJob(asyncJob.jobId)}
                  className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-slate-400/25 bg-card/80 px-3 py-2 text-xs font-medium text-foreground transition-colors duration-150 hover:border-slate-300/45 hover:bg-background"
                >
                  <PauseCircle className="h-3.5 w-3.5" />
                  Pause updates
                </button>
                <span className="text-[11px] text-muted-foreground">Local only</span>
              </div>
            ) : null}

            {asyncJob.status === 'cancelled' ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => resumePendingJob(asyncJob.jobId)}
                  className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-blue-400/25 bg-blue-500/10 px-3 py-2 text-xs font-medium text-blue-100 transition-colors duration-150 hover:border-blue-300/50 hover:bg-blue-500/15"
                >
                  <PlayCircle className="h-3.5 w-3.5" />
                  Resume updates
                </button>
                <span className="text-[11px] text-muted-foreground">Reconnect from this device</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AsyncJobCard;
